import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import * as XLSX from 'xlsx'

/**
 * 📄 EXPORT UTILITIES - VIPLOC BI
 * Utilitários para exportação de relatórios em múltiplos formatos
 */

// ===================================
// 1. EXPORT TO EXCEL (.xlsx)
// ===================================

/**
 * Exporta dados para Excel
 * @param {Array<Object>} data - Array de objetos com os dados
 * @param {String} filename - Nome do arquivo (sem extensão)
 * @param {String} sheetName - Nome da planilha
 */
export const exportToExcel = (data, filename = 'relatorio', sheetName = 'Dados') => {
  try {
    // Cria workbook
    const wb = XLSX.utils.book_new()

    // Converte dados para worksheet
    const ws = XLSX.utils.json_to_sheet(data)

    // Ajusta largura das colunas automaticamente
    const colWidths = []
    const headers = Object.keys(data[0] || {})
    headers.forEach((header, i) => {
      const maxLength = Math.max(
        header.length,
        ...data.map(row => String(row[header] || '').length)
      )
      colWidths[i] = { wch: Math.min(maxLength + 2, 50) }
    })
    ws['!cols'] = colWidths

    // Adiciona worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName)

    // Gera arquivo e faz download
    XLSX.writeFile(wb, `${filename}.xlsx`)

    return { success: true, message: 'Excel gerado com sucesso!' }
  } catch (error) {
    console.error('Erro ao exportar Excel:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Exporta múltiplas planilhas para um único Excel
 * @param {Array<{data, sheetName}>} sheets - Array de objetos com data e sheetName
 * @param {String} filename - Nome do arquivo
 */
export const exportMultiSheetExcel = (sheets, filename = 'relatorio') => {
  try {
    const wb = XLSX.utils.book_new()

    sheets.forEach(sheet => {
      const ws = XLSX.utils.json_to_sheet(sheet.data)
      XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName)
    })

    XLSX.writeFile(wb, `${filename}.xlsx`)

    return { success: true, message: 'Excel com múltiplas planilhas gerado!' }
  } catch (error) {
    console.error('Erro ao exportar multi-sheet Excel:', error)
    return { success: false, error: error.message }
  }
}

// ===================================
// 2. EXPORT TO PDF
// ===================================

/**
 * Exporta elemento HTML para PDF
 * @param {String} elementId - ID do elemento HTML a ser capturado
 * @param {String} filename - Nome do arquivo (sem extensão)
 * @param {Object} options - Opções de exportação
 */
export const exportToPDF = async (elementId, filename = 'relatorio', options = {}) => {
  try {
    const element = document.getElementById(elementId)
    if (!element) {
      throw new Error(`Elemento com ID "${elementId}" não encontrado`)
    }

    // Captura screenshot do elemento
    const canvas = await html2canvas(element, {
      scale: 2, // Qualidade alta
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      ...options.canvasOptions
    })

    // Cria PDF
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({
      orientation: options.orientation || 'portrait',
      unit: 'mm',
      format: options.format || 'a4'
    })

    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = canvas.width
    const imgHeight = canvas.height
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)

    const imgScaledWidth = imgWidth * ratio
    const imgScaledHeight = imgHeight * ratio

    // Centraliza imagem
    const marginX = (pdfWidth - imgScaledWidth) / 2
    const marginY = (pdfHeight - imgScaledHeight) / 2

    // Adiciona cabeçalho se fornecido
    if (options.header) {
      pdf.setFontSize(16)
      pdf.setTextColor(0, 0, 0)
      pdf.text(options.header, pdfWidth / 2, 15, { align: 'center' })
    }

    // Adiciona imagem
    pdf.addImage(
      imgData,
      'PNG',
      marginX,
      options.header ? 25 : marginY,
      imgScaledWidth,
      imgScaledHeight
    )

    // Adiciona rodapé se fornecido
    if (options.footer) {
      pdf.setFontSize(10)
      pdf.setTextColor(128, 128, 128)
      pdf.text(
        options.footer,
        pdfWidth / 2,
        pdfHeight - 10,
        { align: 'center' }
      )
    }

    // Salva PDF
    pdf.save(`${filename}.pdf`)

    return { success: true, message: 'PDF gerado com sucesso!' }
  } catch (error) {
    console.error('Erro ao exportar PDF:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Exporta múltiplos elementos para um PDF de múltiplas páginas
 * @param {Array<String>} elementIds - Array de IDs dos elementos
 * @param {String} filename - Nome do arquivo
 */
export const exportMultiPagePDF = async (elementIds, filename = 'relatorio') => {
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    for (let i = 0; i < elementIds.length; i++) {
      const element = document.getElementById(elementIds[i])
      if (!element) continue

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      })

      const imgData = canvas.toDataURL('image/png')
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      if (i > 0) pdf.addPage()

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
    }

    pdf.save(`${filename}.pdf`)

    return { success: true, message: 'PDF multi-página gerado!' }
  } catch (error) {
    console.error('Erro ao exportar multi-page PDF:', error)
    return { success: false, error: error.message }
  }
}

// ===================================
// 3. EXPORT CHART AS IMAGE (PNG)
// ===================================

/**
 * Exporta gráfico (chart) como imagem PNG
 * @param {String} elementId - ID do elemento contendo o gráfico
 * @param {String} filename - Nome do arquivo (sem extensão)
 */
export const exportChartAsImage = async (elementId, filename = 'grafico') => {
  try {
    const element = document.getElementById(elementId)
    if (!element) {
      throw new Error(`Elemento com ID "${elementId}" não encontrado`)
    }

    const canvas = await html2canvas(element, {
      scale: 3, // Alta qualidade para gráficos
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    })

    // Converte para blob e faz download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${filename}.png`
      link.click()
      URL.revokeObjectURL(url)
    }, 'image/png', 1.0)

    return { success: true, message: 'Imagem gerada com sucesso!' }
  } catch (error) {
    console.error('Erro ao exportar imagem:', error)
    return { success: false, error: error.message }
  }
}

// ===================================
// 4. EXPORT DASHBOARD COMPLETO
// ===================================

/**
 * Exporta dashboard completo com KPIs e gráficos
 * @param {Object} dashboardData - Dados do dashboard
 * @param {String} filename - Nome base dos arquivos
 * @param {String} format - Formato: 'excel', 'pdf', 'both'
 */
export const exportFullDashboard = async (dashboardData, filename = 'dashboard', format = 'both') => {
  try {
    const results = []

    // Prepara dados para Excel
    if (format === 'excel' || format === 'both') {
      const sheets = []

      // Sheet 1: KPIs Principais
      if (dashboardData.kpis) {
        const kpisData = Object.keys(dashboardData.kpis).map(key => ({
          'Métrica': key,
          'Valor': dashboardData.kpis[key]
        }))
        sheets.push({ data: kpisData, sheetName: 'KPIs' })
      }

      // Sheet 2: Top Clientes
      if (dashboardData.top_clients) {
        sheets.push({ data: dashboardData.top_clients, sheetName: 'Top Clientes' })
      }

      // Sheet 3: Receita Mensal
      if (dashboardData.monthly_revenue) {
        sheets.push({ data: dashboardData.monthly_revenue, sheetName: 'Receita Mensal' })
      }

      // Sheet 4: Analytics
      if (dashboardData.analytics) {
        const analyticsData = Object.keys(dashboardData.analytics).map(key => ({
          'Métrica': key,
          'Valor': dashboardData.analytics[key]
        }))
        sheets.push({ data: analyticsData, sheetName: 'Analytics' })
      }

      const excelResult = exportMultiSheetExcel(sheets, `${filename}_dados`)
      results.push(excelResult)
    }

    // Exporta PDF do dashboard visual
    if (format === 'pdf' || format === 'both') {
      const pdfResult = await exportToPDF('dashboard-container', `${filename}_visual`, {
        header: 'VIPLOC System - Dashboard BI',
        footer: `Gerado em: ${new Date().toLocaleString('pt-BR')}`,
        orientation: 'landscape'
      })
      results.push(pdfResult)
    }

    return {
      success: results.every(r => r.success),
      message: 'Dashboard exportado com sucesso!',
      results
    }
  } catch (error) {
    console.error('Erro ao exportar dashboard completo:', error)
    return { success: false, error: error.message }
  }
}

// ===================================
// 5. UTILITÁRIOS AUXILIARES
// ===================================

/**
 * Formata data para nome de arquivo
 */
export const getDateString = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hour = String(now.getHours()).padStart(2, '0')
  const minute = String(now.getMinutes()).padStart(2, '0')
  return `${year}${month}${day}_${hour}${minute}`
}

/**
 * Gera nome de arquivo com timestamp
 */
export const getFilenameWithTimestamp = (baseName) => {
  return `${baseName}_${getDateString()}`
}

/**
 * Mostra notificação de sucesso/erro
 */
export const showExportNotification = (result, type = 'Excel') => {
  if (result.success) {
    alert(`✅ ${type} exportado com sucesso!`)
  } else {
    alert(`❌ Erro ao exportar ${type}: ${result.error}`)
  }
}
